/**
 * Price Update Approvals
 * ══════════════════════
 * Admin / Super-Admin dashboard for reviewing manager price-change submissions.
 * Displays existing → proposed price, photo proof, and Approve / Reject actions.
 *
 * Location: Management → Price Update Approvals.
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Check, X, RefreshCw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PriceUpdateApprovals() {
  const { session } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [preview, setPreview] = useState(null);       // { url, row }

  const load = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/price-updates/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, status: status || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Load failed");
      setRows(d.rows || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [session?.token, status]);
  useEffect(() => { load(); }, [load]);

  const proofUrl = (r) => `${API}/api/consumption/price-proofs/${r.proof_id}.${r.proof_ext}?token=${encodeURIComponent(session.token)}`;

  const decide = async (r, action) => {
    let reason = null, overridePrice = null;
    if (action === "reject") {
      reason = window.prompt("Rejection reason?");
      if (!reason) return;
    } else if (action === "edit_approve") {
      const p = window.prompt(`Approve at what price? (proposed: ${r.new_price})`, r.new_price);
      if (!p || isNaN(Number(p))) return;
      overridePrice = Number(p);
      action = "approve";
    }
    try {
      const resp = await fetch(`${API}/api/consumption/price-updates/decide`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, update_id: r._id, action,
          reason, override_price: overridePrice,
        }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || "Failed");
      toast.success(`${action === "approve" ? "Approved" : "Rejected"}: ${r.item_name}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const statusBadge = (s) => {
    if (s === "pending") return <Badge className="bg-amber-100 text-amber-800 border-amber-300">Pending</Badge>;
    if (s === "approved") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Approved</Badge>;
    if (s === "rejected") return <Badge className="bg-rose-100 text-rose-800 border-rose-300">Rejected</Badge>;
    if (s === "auto_approved") return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Auto</Badge>;
    return <Badge>{s}</Badge>;
  };

  return (
    <div className="p-4 space-y-4" data-testid="price-approvals-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          Price Update Approvals
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Review and approve Center-Manager price-change submissions. Approving refreshes
          <code className="mx-1">current_wap</code> for the specific centre and records a price-history entry.
        </p>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Status</Label>
          <select value={status} onChange={e => setStatus(e.target.value)}
                  className="h-8 border rounded px-2 text-xs" data-testid="pa-status">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="auto_approved">Auto-approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="text-xs" data-testid="pa-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
      </CardContent></Card>

      <Card><CardContent className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Center</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Existing</TableHead>
                <TableHead className="text-right">Proposed</TableHead>
                <TableHead className="text-right">Δ %</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Proof</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-6">
                  No records match this filter.
                </TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r._id} data-testid={`pa-row-${r._id}`}>
                  <TableCell className="font-semibold">{r.center}</TableCell>
                  <TableCell>
                    <div className="font-semibold">{r.item_name}</div>
                    <div className="text-[10px] text-slate-400">{r.item_code}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono">₹{r.existing_price?.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono font-bold">₹{r.new_price?.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {r.difference_pct != null ? `${r.difference_pct}%` : "—"}
                  </TableCell>
                  <TableCell>{r.vendor || "—"}</TableCell>
                  <TableCell>
                    {r.proof_id ? (
                      <Button size="sm" variant="ghost"
                              onClick={() => setPreview({ url: proofUrl(r), row: r })}
                              data-testid={`pa-view-proof-${r._id}`}>
                        <ImageIcon className="w-3 h-3 mr-1" /> View
                      </Button>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{r.submitted_by_name}</TableCell>
                  <TableCell className="text-[10px]">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-right">
                    {r.status === "pending" ? (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                                onClick={() => decide(r, "approve")} data-testid={`pa-approve-${r._id}`}>
                          <Check className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => decide(r, "edit_approve")} data-testid={`pa-edit-approve-${r._id}`}
                                title="Edit and Approve">
                          ✎
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-700 border-rose-300"
                                onClick={() => decide(r, "reject")} data-testid={`pa-reject-${r._id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      {/* Proof preview */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => setPreview(null)}>
          <div className="max-w-3xl max-h-[90vh] bg-white rounded p-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs">
                <strong>{preview.row.item_name}</strong> — {preview.row.center} · ₹{preview.row.existing_price} → ₹{preview.row.new_price}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>✕</Button>
            </div>
            {preview.row.proof_ext === "pdf" ? (
              <iframe src={preview.url} className="w-full h-[70vh]" title="Proof PDF" />
            ) : (
              <img src={preview.url} alt="Price proof" className="max-w-full max-h-[70vh] object-contain mx-auto" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
